import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useModal } from '../../context/ModalContext'
import { useChatEvents } from '../../hooks/useChatEvents'
import {
  deleteChatMessage,
  editChatMessage,
  getChatroom,
  likeChatMessage,
  listChatReplies,
  sendChatMessage,
  sendChatReply,
  unlikeChatMessage,
} from '../../services/chatrooms'
import { emitChat } from '../../services/chatSocket'
import ChatComposer from './ChatComposer'
import MessageCard from './ChatMessage'
import './Chatroom.css'

function mineOf(message, userId) {
  return { ...message, mine: String(message.author?._id) === String(userId) }
}

function tokensOf(query) {
  return query.toLowerCase().split(/\s+/).filter(Boolean)
}

function ChatWindowBody({ chatId, revision, onRoom, onMissing }) {
  const { user } = useAuth()
  const { openModal } = useModal()
  const [room, setRoom] = useState(null)
  const [messages, setMessages] = useState([])
  const [replies, setReplies] = useState({})
  const [openReplies, setOpenReplies] = useState({})
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [typers, setTypers] = useState([])
  const endRef = useRef(null)
  const seenRef = useRef(new Set())
  const onRoomRef = useRef(onRoom)
  const onMissingRef = useRef(onMissing)
  onRoomRef.current = onRoom
  onMissingRef.current = onMissing

  useEffect(() => {
    if (!chatId) {
      setRoom(null)
      setMessages([])
      setStatus('idle')
      return undefined
    }
    let alive = true
    seenRef.current = new Set()
    setStatus('loading')
    setError('')
    getChatroom(chatId)
      .then((data) => {
        if (!alive) return
        setRoom(data.room)
        setMessages((data.messages || []).map((message) => mineOf(message, user?._id)))
        setReplies({})
        setOpenReplies({})
        setStatus('ready')
        onRoomRef.current?.(data.room)
      })
      .catch((err) => {
        if (!alive) return
        setStatus('error')
        setError(err.message || 'Could not open this chat.')
        if (/not in this chat|not found/i.test(err.message || '')) onMissingRef.current?.(chatId)
      })
    return () => {
      alive = false
    }
  }, [chatId, revision, user?._id])

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, chatId])

  const patchMessage = (messageId, change) => {
    setMessages((prev) => prev.map((message) => (String(message._id) === String(messageId) ? change(message) : message)))
    setReplies((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((key) => {
        if (!Array.isArray(next[key])) return
        next[key] = next[key].map((message) =>
          String(message._id) === String(messageId) ? change(message) : message
        )
      })
      return next
    })
  }

  useChatEvents((name, payload) => {
    if (!chatId || String(payload.chatroomId) !== String(chatId)) return
    const me = String(user?._id || '')

    if (name === 'room:delete' || (name === 'room:membership' && payload.action === 'removed' && String(payload.userId) === me)) {
      onMissingRef.current?.(chatId)
      return
    }
    if (name === 'room:update' && payload.room) {
      setRoom(payload.room)
      onRoomRef.current?.(payload.room)
      return
    }
    if (name === 'message:new' && payload.message && !payload.message.parent) {
      const next = mineOf(payload.message, me)
      setMessages((prev) => (prev.some((message) => String(message._id) === String(next._id)) ? prev : [...prev, next]))
      return
    }
    if (name === 'message:edit' && payload.message) {
      patchMessage(payload.message._id, (current) => ({
        ...current,
        text: payload.message.text,
        items: payload.message.items,
        guides: payload.message.guides,
      }))
      return
    }
    if (name === 'message:delete') {
      setMessages((prev) => prev.filter((message) => String(message._id) !== String(payload.messageId)))
      return
    }
    if (name === 'reply:new' && payload.reply) {
      const reply = mineOf(payload.reply, me)
      const fresh = !seenRef.current.has(String(reply._id))
      seenRef.current.add(String(reply._id))
      if (fresh) {
        setMessages((prev) =>
          prev.map((message) =>
            String(message._id) === String(payload.parentId)
              ? { ...message, replyCount: (message.replyCount || 0) + 1 }
              : message
          )
        )
      }
      setReplies((prev) => {
        const list = prev[payload.parentId]
        if (!Array.isArray(list)) return prev
        if (list.some((entry) => String(entry._id) === String(reply._id))) return prev
        return { ...prev, [payload.parentId]: [...list, reply] }
      })
      return
    }
    if (name === 'reply:edit' && payload.message) {
      patchMessage(payload.message._id, (current) => ({ ...current, text: payload.message.text }))
      return
    }
    if (name === 'reply:delete') {
      setMessages((prev) =>
        prev.map((message) =>
          String(message._id) === String(payload.parentId)
            ? { ...message, replyCount: Math.max(0, (message.replyCount || 1) - 1) }
            : message
        )
      )
      setReplies((prev) => {
        const list = prev[payload.parentId]
        if (!Array.isArray(list)) return prev
        return {
          ...prev,
          [payload.parentId]: list.filter((entry) => String(entry._id) !== String(payload.messageId)),
        }
      })
      return
    }
    if (name === 'message:like' || name === 'message:unlike') {
      patchMessage(payload.messageId, (current) => ({
        ...current,
        likeCount: payload.likeCount,
        liked: String(payload.userId) === me ? name === 'message:like' : current.liked,
      }))
      return
    }
    if (name === 'typing' && String(payload.userId) !== me) {
      setTypers((prev) => {
        const rest = prev.filter((entry) => entry.userId !== payload.userId)
        return [...rest, { userId: payload.userId, displayName: payload.displayName || 'Member' }]
      })
      return
    }
    if (name === 'typing:stop') {
      setTypers((prev) => prev.filter((entry) => entry.userId !== payload.userId))
    }
  })

  const toggleReplies = async (message) => {
    const id = message._id
    if (openReplies[id]) {
      setOpenReplies((prev) => ({ ...prev, [id]: false }))
      return
    }
    setOpenReplies((prev) => ({ ...prev, [id]: true }))
    if (Array.isArray(replies[id])) return
    setReplies((prev) => ({ ...prev, [id]: 'loading' }))
    try {
      const list = await listChatReplies(chatId, id)
      setReplies((prev) => ({ ...prev, [id]: (list || []).map((entry) => mineOf(entry, user?._id)) }))
    } catch (err) {
      setReplies((prev) => ({ ...prev, [id]: [] }))
      setError(err.message || 'Could not load replies.')
    }
  }

  const onLike = async (message) => {
    const action = message.liked ? unlikeChatMessage : likeChatMessage
    try {
      const result = await action(chatId, message._id)
      patchMessage(message._id, (current) => ({
        ...current,
        liked: Boolean(result.liked),
        likeCount: result.likeCount,
      }))
    } catch (err) {
      setError(err.message || 'Could not update the like.')
    }
  }

  const onEdit = async (message, text) => {
    try {
      const next = await editChatMessage(chatId, message._id, { text })
      patchMessage(message._id, (current) => mineOf({ ...current, ...next }, user?._id))
    } catch (err) {
      setError(err.message || 'Could not edit that message.')
    }
  }

  const onDelete = (message) => {
    openModal('confirm', {
      title: 'Delete message',
      message: 'This removes the message for everyone in the chat.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => deleteChatMessage(chatId, message._id),
      onConfirmed: () => {
        if (message.parent) {
          setReplies((prev) => {
            const list = prev[message.parent]
            if (!Array.isArray(list)) return prev
            return {
              ...prev,
              [message.parent]: list.filter((entry) => entry._id !== message._id),
            }
          })
          setMessages((prev) =>
            prev.map((entry) =>
              String(entry._id) === String(message.parent)
                ? { ...entry, replyCount: Math.max(0, (entry.replyCount || 1) - 1) }
                : entry
            )
          )
        } else {
          setMessages((prev) => prev.filter((entry) => entry._id !== message._id))
        }
      },
    })
  }

  const onReply = async (message, text) => {
    try {
      const reply = await sendChatReply(chatId, message._id, text)
      const shaped = mineOf(reply, user?._id)
      const fresh = !seenRef.current.has(String(shaped._id))
      seenRef.current.add(String(shaped._id))
      if (fresh) {
        setMessages((prev) =>
          prev.map((entry) =>
            String(entry._id) === String(message._id)
              ? { ...entry, replyCount: (entry.replyCount || 0) + 1 }
              : entry
          )
        )
      }
      setOpenReplies((prev) => ({ ...prev, [message._id]: true }))
      setReplies((prev) => {
        const list = Array.isArray(prev[message._id]) ? prev[message._id] : []
        if (list.some((entry) => String(entry._id) === String(shaped._id))) return { ...prev, [message._id]: list }
        return { ...prev, [message._id]: [...list, shaped] }
      })
    } catch (err) {
      setError(err.message || 'Could not send the reply.')
    }
  }

  const onSend = async ({ text, items, guides }) => {
    try {
      const result = await sendChatMessage(chatId, {
        text,
        items: items.map((item) => item._id),
        guides: guides.map((guide) => guide._id),
      })
      const next = mineOf(result.message, user?._id)
      setMessages((prev) => (prev.some((entry) => entry._id === next._id) ? prev : [...prev, next]))
    } catch (err) {
      setError(err.message || 'Could not send the message.')
    }
  }

  if (!chatId) return <div className="display-empty">Select a chat from the list.</div>
  if (status === 'loading' && !room) return <div className="display-empty">Loading…</div>
  if (status === 'error' && !room) return <div className="display-empty">{error}</div>

  const words = tokensOf(query)
  const shown = words.length
    ? messages.filter((message) => words.every((word) => (message.text || '').toLowerCase().includes(word)))
    : messages
  const typingLabel = typers.map((entry) => entry.displayName).filter(Boolean)

  return (
    <div className="crm-window">
      <div className="crm-search">
        <input
          className="ct-input"
          type="text"
          value={query}
          placeholder="Search messages"
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="crm-thread">
        {error ? <div className="crm-status">{error}</div> : null}
        {shown.length === 0 ? (
          <div className="crm-status">{words.length ? 'No messages match.' : 'No messages yet.'}</div>
        ) : (
          shown.map((message) => (
            <MessageCard
              key={message._id}
              message={message}
              replies={replies[message._id]}
              repliesOpen={Boolean(openReplies[message._id])}
              onToggleReplies={toggleReplies}
              onReply={onReply}
              onLike={onLike}
              onEdit={onEdit}
              onDelete={onDelete}
              onLikes={(entry) =>
                openModal('chat-likes', { chatroomId: chatId, messageId: entry._id })
              }
            />
          ))
        )}
        <div ref={endRef} />
      </div>
      {typingLabel.length ? (
        <div className="crm-typing">
          {typingLabel.join(', ')} {typingLabel.length === 1 ? 'is' : 'are'} typing
        </div>
      ) : null}
      <ChatComposer
        key={chatId}
        disabled={status !== 'ready'}
        onSend={onSend}
        onTyping={(active) => emitChat(active ? 'typing' : 'typing:stop', { chatroomId: chatId })}
      />
    </div>
  )
}

export default ChatWindowBody
